import { uniq } from "lodash";
import { useEffect, useMemo, type FC } from "react";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";

import {
  selectProjectsByIds,
  selectProjectsIsLoading,
} from "@ds/modules/settings/projects/redux/selectors/common-selectors";
import { projectsActions } from "@ds/modules/settings/projects/redux/slice";

import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { InfoSection } from "@ds/components/info-view";
import { SpinnerLoader2 } from "@ds/components/loaders/loaders";
import { TextLink } from "@ds/components/text-link";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { selectInvitationsByIds } from "../../redux/selectors";

export const InvitationProjectsInfoSection: FC<{ invitation: Invitation }> = ({ invitation }) => {
  const aggregatedInvitationIds = useGetAggregatedEntityIds(invitation);
  const [aggregatedInvitationProjectsIds, aggregatedInvitationProjects] = useDeepRootSelector(state => {
    const aggregatedInvitations = selectInvitationsByIds(state, aggregatedInvitationIds);
    const aggregatedProjectsIds = uniq(aggregatedInvitations.flatMap(({ project_ids }) => project_ids));
    return [aggregatedProjectsIds, selectProjectsByIds(state, aggregatedProjectsIds)] as const;
  });

  const isLoading = useRootSelector(selectProjectsIsLoading);

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (aggregatedInvitationProjectsIds.length) {
      dispatch(projectsActions.fetchProjects({ id: aggregatedInvitationProjectsIds }));
    }
  }, [dispatch, aggregatedInvitationProjects, aggregatedInvitationProjectsIds]);

  const infoBody = useMemo(() => {
    if (isLoading) {
      return <SpinnerLoader2 />;
    }

    const hasMultipleValues = isMultipleFieldValuesInAggregatedEntity(invitation, "project_ids");
    if (!aggregatedInvitationProjects.length) {
      return <UnassignedLabel text="Invitation(s) has no assigned projects" />;
    } else if (hasMultipleValues) {
      return <MultipleValuesLabelWithLink to={composeDetailsRouteByEntityType(aggregatedInvitationProjects)} />;
    }

    return aggregatedInvitationProjects?.map(entry => (
      <div key={`invitation-info-project-${entry.id}`} className="mb-1">
        <TextLink to={composeDetailsRouteByEntityType(entry)}>{entry.name}</TextLink>
      </div>
    ));
  }, [isLoading, invitation, aggregatedInvitationProjects]);

  return (
    <InfoSection title="Projects" icon="pi pi-list">
      {infoBody}
    </InfoSection>
  );
};
